import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GoalsCard from '../components/GoalsPopup';
import authFetch from '../utils/authFetch';
import { resolveContactConflictResponse } from '../utils/contactResolution';
import { SELLING_PROPERTY_TYPE_OPTIONS, SELLING_PROPERTY_REAL_ESTATE_TYPE_OPTIONS, normalizeSellingPropertyType } from '../utils/sellingPropertyTypes';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const sendWithContactResolution = async (makeRequest, initialPayload) => {
  let payload = initialPayload;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const response = await makeRequest(payload);
    if (response.ok) {
      return response;
    }

    const conflict = await resolveContactConflictResponse(response, payload);
    if (!conflict.shouldRetry) {
      if (conflict.errorData) {
        response.copilotErrorData = conflict.errorData;
      }
      return response;
    }

    payload = conflict.payload;
  }

  return makeRequest(payload);
};

const getResponseErrorMessage = async (response, fallbackMessage) => {
  if (!response) return fallbackMessage;

  if (response.copilotErrorData?.error) {
    return response.copilotErrorData.error;
  }

  try {
    const rawText = await response.clone().text();
    if (!rawText) return fallbackMessage;

    try {
      const parsed = JSON.parse(rawText);
      return parsed.error || parsed.message || fallbackMessage;
    } catch (error) {
      return rawText;
    }
  } catch (error) {
    return fallbackMessage;
  }
};

const createOwnerDraft = (name = '') => ({ name });

const ownerDraftsToText = (owners = []) => owners
  .map((owner) => String(owner?.name || '').trim())
  .filter(Boolean)
  .join('\n');

const ownerTextToDrafts = (value = '') => {
  const drafts = String(value || '')
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((name) => createOwnerDraft(name));

  return drafts.length > 0 ? drafts : [createOwnerDraft()];
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isUserB = localStorage.getItem('leadGenUser') === 'B';
  const [leadStats, setLeadStats] = useState({ total: 0, lettersSent: 0, inSelling: 0, responded: 0 });
  const [leadStatsFilters, setLeadStatsFilters] = useState(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return { creator: '', month: currentMonth, type: '' };
  });
  const [leadStatsOptions, setLeadStatsOptions] = useState({ creators: [], months: [], types: [] });
  const [leadStatsLoading, setLeadStatsLoading] = useState(false);
  const [leadStatsError, setLeadStatsError] = useState('');
  const [sellingEntries, setSellingEntries] = useState([]);
  const [sellingLoading, setSellingLoading] = useState(false);
  const [sellingError, setSellingError] = useState('');
  const [sourceOptions, setSourceOptions] = useState(() => {
    try {
      const stored = localStorage.getItem('sellingSourceOptions');
      const parsed = stored ? JSON.parse(stored) : null;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch (err) {
      console.warn('Failed to read source options', err);
    }
    return ['dopisi'];
  });
  const [showings, setShowings] = useState([]);
  const [showingsLoading, setShowingsLoading] = useState(false);
  const [showingsError, setShowingsError] = useState('');
  const [showShowingsModal, setShowShowingsModal] = useState(false);
  const [showingsFilter, setShowingsFilter] = useState({ year: '', month: '' });
  const [showAddShowingModal, setShowAddShowingModal] = useState(false);
  const [showingsEditMode, setShowingsEditMode] = useState(false);
  const [editingShowings, setEditingShowings] = useState({});
  const [showingForm, setShowingForm] = useState({
    sellingPropertyId: null,
    showingDateTime: '',
    notes: '',
    people: [{ name: '', email: '', phone: '' }]
  });
  const [soldEntries, setSoldEntries] = useState([]);
  const [soldLoading, setSoldLoading] = useState(false);
  const [soldError, setSoldError] = useState('');
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [showSoldDetailsModal, setShowSoldDetailsModal] = useState(false);
  const [selectedSoldEntry, setSelectedSoldEntry] = useState(null);
  const [showSellingModal, setShowSellingModal] = useState(false);
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(false);
  const [sellingTableFilter, setSellingTableFilter] = useState('exclusive');
  const [sellingStatsTab, setSellingStatsTab] = useState('pridobivanje');
  const [sellingStatsFilter, setSellingStatsFilter] = useState(() => {
    const now = new Date();
    return { source: '', year: String(now.getFullYear()), month: String(now.getMonth() + 1) };
  });
  const [sellingStats, setSellingStats] = useState({ count: 0, sumPrice: 0, sumGross: 0, sumNet: 0, avgGross: 0, avgGrossPercent: 0, sources: [], years: [] });
  const [sellingStatsLoading, setSellingStatsLoading] = useState(false);
  const [markSoldForm, setMarkSoldForm] = useState({
    price: '',
    grossCommission: '',
    netCommission: '',
    grossCommissionType: '€',
    netCommissionType: '€',
    grossCommissionInput: '',
    netCommissionInput: '',
    buyersText: ''
  });
  const [isNewSellingEntry, setIsNewSellingEntry] = useState(false);
  const [sellingForm, setSellingForm] = useState({
    id: null,
    leadId: null,
    katastrskaObcina: '',
    idParceleStavbe: '',
    type: '',
    propertyType: '',
    owners: [createOwnerDraft()],
    ownersText: '',
    buyersText: '',
    createdAt: null,
    surfaceArea: '',
    advertisingEnabled: true,
    adLink: '',
    price: '',
    grossCommission: '',
    grossCommissionType: '€',
    grossCommissionInput: '',
    netCommission: '',
    netCommissionType: '€',
    netCommissionInput: '',
    source: ''
  });

  const fetchLeadStatsOptions = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/properties/stats/options`);
      if (!response.ok) throw new Error('Failed to load stats options');
      const data = await response.json();
      setLeadStatsOptions({
        creators: data.creators || [],
        months: data.months || [],
        types: data.types || []
      });
    } catch (err) {
      console.error('Error fetching lead stats options:', err);
      setLeadStatsError('Could not load lead stats options.');
    }
  }, []);

  const fetchLeadStats = useCallback(async () => {
    setLeadStatsLoading(true);
    setLeadStatsError('');
    try {
      const params = new URLSearchParams();
      if (leadStatsFilters.creator) params.set('creator', leadStatsFilters.creator);
      if (leadStatsFilters.month) params.set('month', leadStatsFilters.month);
      if (leadStatsFilters.type) params.set('type', leadStatsFilters.type);

      const url = `${API_BASE}/api/leads/properties/stats${params.toString() ? `?${params}` : ''}`;
      const response = await authFetch(url);
      if (!response.ok) throw new Error('Failed to load lead stats');
      const data = await response.json();
      setLeadStats({
        total: data.total || 0,
        lettersSent: data.letters_sent || data.lettersSent || 0,
        inSelling: data.in_selling || data.inSelling || 0,
        responded: data.responded || 0
      });
    } catch (err) {
      console.error('Error fetching lead stats:', err);
      setLeadStatsError('Could not load lead stats.');
    } finally {
      setLeadStatsLoading(false);
    }
  }, [leadStatsFilters]);

  useEffect(() => {
    fetchLeadStatsOptions();
  }, [fetchLeadStatsOptions]);

  useEffect(() => {
    fetchLeadStats();
  }, [fetchLeadStats]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSellingStats = useCallback(async () => {
    setSellingStatsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tab', sellingStatsTab);
      if (sellingStatsFilter.source) params.set('source', sellingStatsFilter.source);
      if (sellingStatsFilter.year) params.set('year', sellingStatsFilter.year);
      if (sellingStatsFilter.month) params.set('month', sellingStatsFilter.month);
      const response = await authFetch(`${API_BASE}/api/leads/selling/stats?${params}`);
      if (!response.ok) throw new Error('Failed to load selling stats');
      const data = await response.json();
      setSellingStats(data);
    } catch (err) {
      console.error('Error fetching selling stats:', err);
    } finally {
      setSellingStatsLoading(false);
    }
  }, [sellingStatsTab, sellingStatsFilter]);

  useEffect(() => {
    if (isUserB) fetchSellingStats();
  }, [fetchSellingStats, isUserB]);

  const fetchSellingEntries = useCallback(async () => {
    setSellingLoading(true);
    setSellingError('');
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling?status=selling`);
      if (!response.ok) throw new Error('Failed to load selling entries');
      const data = await response.json();
      setSellingEntries(data);
    } catch (err) {
      console.error('Error fetching selling entries:', err);
      setSellingError('Could not load selling entries.');
    } finally {
      setSellingLoading(false);
    }
  }, []);

  const fetchSoldEntries = useCallback(async () => {
    setSoldLoading(true);
    setSoldError('');
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling?status=sold`);
      if (!response.ok) throw new Error('Failed to load sold entries');
      const data = await response.json();
      setSoldEntries(data);
    } catch (err) {
      console.error('Error fetching sold entries:', err);
      setSoldError('Could not load sold entries.');
    } finally {
      setSoldLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellingEntries();
  }, [fetchSellingEntries]);

  useEffect(() => {
    if (showSoldModal) {
      fetchSoldEntries();
    }
  }, [fetchSoldEntries, showSoldModal]);

  const openSellingModal = (entry, action = '') => {
    const params = new URLSearchParams();
    if (action) {
      params.set('action', action);
    }

    navigate(`/selling/${entry.id}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const openNewSellingModal = () => {
    setIsNewSellingEntry(true);
    setSellingForm({
      id: null,
      leadId: null,
      katastrskaObcina: '',
      idParceleStavbe: '',
      type: '',
      propertyType: '',
      owners: [createOwnerDraft()],
      ownersText: '',
      buyersText: '',
      source: sourceOptions[0] || '',
      createdAt: null,
      surfaceArea: '',
      advertisingEnabled: true,
      adLink: '',
      price: '',
      grossCommission: '',
      grossCommissionType: '€',
      netCommission: '',
      netCommissionType: '€',
      grossCommissionInput: '',
      netCommissionInput: ''
    });
    setShowSellingModal(true);
  };

  const openSoldDetailsModal = (entry) => {
    navigate(`/selling/${entry.id}`);
  };

  const fetchShowingsForEntry = async (sellingPropertyId) => {
    setShowingsLoading(true);
    setShowingsError('');
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${sellingPropertyId}/showings`);
      if (!response.ok) throw new Error('Failed to load showings');
      const data = await response.json();
      setShowings(data);
    } catch (err) {
      console.error('Error fetching showings:', err);
      setShowingsError('Could not load showings.');
    } finally {
      setShowingsLoading(false);
    }
  };

  const fetchShowingsList = useCallback(async () => {
    setShowingsLoading(true);
    setShowingsError('');
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/showings`);
      if (!response.ok) throw new Error('Failed to load showings');
      const data = await response.json();
      setShowings(data);
    } catch (err) {
      console.error('Error fetching showings list:', err);
      setShowingsError('Could not load showings.');
    } finally {
      setShowingsLoading(false);
    }
  }, []);

  const openAddShowingModal = (entry) => {
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setShowingForm({
      sellingPropertyId: entry.id,
      showingDateTime: localDateTime,
      notes: '',
      people: [{ name: '', email: '', phone: '' }]
    });
    setShowAddShowingModal(true);
  };

  const handleSaveShowing = async () => {
    const validPeople = (showingForm.people || []).filter((person) => person.name.trim());
    if (!showingForm.showingDateTime || validPeople.length === 0) {
      alert('Please enter date/time and at least one name.');
      return;
    }
    try {
      const requests = validPeople.map((person) =>
        sendWithContactResolution(
          (nextPayload) => authFetch(`${API_BASE}/api/leads/selling/${showingForm.sellingPropertyId}/showings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nextPayload)
          }),
          {
            showingDateTime: showingForm.showingDateTime,
            name: person.name,
            notes: showingForm.notes,
            email: person.email,
            phone: person.phone
          }
        )
      );

      const responses = await Promise.all(requests);
      if (responses.some((response) => !response.ok)) throw new Error('Failed to create showing');
      await fetchShowingsForEntry(showingForm.sellingPropertyId);
      setShowAddShowingModal(false);
    } catch (err) {
      console.error('Error creating showing:', err);
      alert('Could not create showing.');
    }
  };

  const handleEditShowing = (showing) => {
    setEditingShowings((prev) => ({
      ...prev,
      [showing.id]: {
        showingDateTime: showing.showingDateTime ? new Date(new Date(showing.showingDateTime).getTime() - new Date(showing.showingDateTime).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
        name: showing.name || '',
        email: showing.email || '',
        phone: showing.phone || '',
        notes: showing.notes || ''
      }
    }));
  };

  const handleSaveEditedShowing = async (showingId) => {
    const data = editingShowings[showingId];
    if (!data || !data.showingDateTime || !data.name) {
      alert('Date/time and name are required.');
      return;
    }
    try {
      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/selling/showings/${showingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        {
          showingDateTime: data.showingDateTime,
          name: data.name,
          notes: data.notes,
          email: data.email,
          phone: data.phone
        }
      );
      if (!response.ok) throw new Error('Failed to update showing');
      setEditingShowings((prev) => {
        const next = { ...prev };
        delete next[showingId];
        return next;
      });
      await fetchShowingsList();
    } catch (err) {
      console.error('Error updating showing:', err);
      alert('Could not update showing.');
    }
  };

  const handleDeleteShowing = async (showingId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Delete this ogled?')) return;
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/showings/${showingId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete showing');
      await fetchShowingsList();
    } catch (err) {
      console.error('Error deleting showing:', err);
      alert('Could not delete showing.');
    }
  };

  const handleSaveSellingEntry = async () => {
    if (!sellingForm.katastrskaObcina || !sellingForm.idParceleStavbe || !sellingForm.type) {
      alert('Please fill in required fields.');
      return;
    }

    const validOwners = (sellingForm.owners || []).map((owner) => ({ name: String(owner?.name || '').trim() })).filter((owner) => owner.name);
    if (validOwners.length === 0) {
      alert('Please enter at least one owner name.');
      return;
    }

    const grossInputValue = sellingForm.grossCommissionType === '%' ? sellingForm.grossCommissionInput : sellingForm.grossCommission;
    const netInputValue = sellingForm.netCommissionType === '%' ? sellingForm.netCommissionInput : sellingForm.netCommission;

    const computedGross = computeCommissionValue({
      value: grossInputValue,
      type: sellingForm.grossCommissionType,
      base: sellingForm.price
    });

    if (grossInputValue && sellingForm.grossCommissionType === '%' && computedGross === null) {
      alert('Please enter price to compute gross commission percent.');
      return;
    }

    const computedNet = computeCommissionValue({
      value: netInputValue,
      type: sellingForm.netCommissionType,
      base: computedGross
    });

    if (netInputValue && sellingForm.netCommissionType === '%' && computedNet === null) {
      alert('Please enter gross commission to compute net commission percent.');
      return;
    }

    const payload = {
      leadId: sellingForm.leadId,
      katastrskaObcina: sellingForm.katastrskaObcina,
      idParceleStavbe: sellingForm.idParceleStavbe,
      type: sellingForm.type,
      propertyType: sellingForm.propertyType,
      owners: validOwners,
      ownersText: ownerDraftsToText(validOwners),
      source: sellingForm.source,
      surfaceArea: sellingForm.surfaceArea,
      advertisingEnabled: sellingForm.advertisingEnabled,
      adLink: sellingForm.adLink,
      price: sellingForm.price,
      grossCommission: computedGross ?? '',
      netCommission: computedNet ?? '',
      grossCommissionInput: grossInputValue ?? '',
      grossCommissionInputType: sellingForm.grossCommissionType,
      netCommissionInput: netInputValue ?? '',
      netCommissionInputType: sellingForm.netCommissionType,
      createdAt: sellingForm.createdAt || null
    };

    try {
      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(
          isNewSellingEntry
            ? `${API_BASE}/api/leads/selling`
            : `${API_BASE}/api/leads/selling/${sellingForm.id}`,
          {
            method: isNewSellingEntry ? 'POST' : 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(nextPayload)
          }
        ),
        payload
      );

      if (!response.ok) {
        const message = await getResponseErrorMessage(response, 'Failed to save entry');
        throw new Error(message);
      }
      await fetchSellingEntries();
      setShowSellingModal(false);
    } catch (err) {
      console.error('Error saving selling entry:', err);
      alert(err.message || 'Could not save entry.');
    }
  };

  const handleDeleteSellingEntry = async () => {
    if (!sellingForm.id) return;
    if (!window.confirm('Delete this entry?')) return;
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${sellingForm.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete entry');
      await fetchSellingEntries();
      setShowSellingModal(false);
    } catch (err) {
      console.error('Error deleting selling entry:', err);
      alert('Could not delete entry.');
    }
  };

  const handleConfirmMarkSold = async () => {
    if (!sellingForm.id) return;
    const grossInputValue = markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission;
    const netInputValue = markSoldForm.netCommissionType === '%' ? markSoldForm.netCommissionInput : markSoldForm.netCommission;

    const computedGross = computeCommissionValue({
      value: grossInputValue,
      type: markSoldForm.grossCommissionType,
      base: markSoldForm.price
    });

    if (grossInputValue && markSoldForm.grossCommissionType === '%' && computedGross === null) {
      alert('Please enter price to compute gross commission percent.');
      return;
    }

    const computedNet = computeCommissionValue({
      value: netInputValue,
      type: markSoldForm.netCommissionType,
      base: computedGross
    });

    if (netInputValue && markSoldForm.netCommissionType === '%' && computedNet === null) {
      alert('Please enter gross commission to compute net commission percent.');
      return;
    }
    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${sellingForm.id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'sold',
          price: markSoldForm.price,
          grossCommission: computedGross ?? '',
          netCommission: computedNet ?? '',
          buyersText: markSoldForm.buyersText,
          grossCommissionInput: grossInputValue ?? '',
          grossCommissionInputType: markSoldForm.grossCommissionType,
          netCommissionInput: netInputValue ?? '',
          netCommissionInputType: markSoldForm.netCommissionType
        })
      });
      if (!response.ok) {
        const fallback = await authFetch(`${API_BASE}/api/leads/selling/${sellingForm.id}/status?status=sold`);
        if (!fallback.ok) throw new Error('Failed to mark as sold');
      }
      await fetchSellingEntries();
      setShowMarkSoldModal(false);
      setShowSellingModal(false);
    } catch (err) {
      console.error('Error marking as sold:', err);
      alert('Could not mark as sold.');
    }
  };

  const handleUpdateSoldSource = async (updatedSource) => {
    if (!selectedSoldEntry) return;
    try {
      const payload = {
        katastrskaObcina: selectedSoldEntry.katastrskaObcina,
        idParceleStavbe: selectedSoldEntry.idParceleStavbe,
        type: selectedSoldEntry.type,
        ownersText: selectedSoldEntry.ownersText || selectedSoldEntry.ownerNames || '',
        buyersText: selectedSoldEntry.buyersText || '',
        source: updatedSource,
        price: selectedSoldEntry.price ?? '',
        grossCommission: selectedSoldEntry.grossCommission ?? '',
        netCommission: selectedSoldEntry.netCommission ?? '',
        grossCommissionInput: selectedSoldEntry.grossCommissionInput ?? '',
        grossCommissionInputType: selectedSoldEntry.grossCommissionInputType || '€',
        netCommissionInput: selectedSoldEntry.netCommissionInput ?? '',
        netCommissionInputType: selectedSoldEntry.netCommissionInputType || '€'
      };

      const response = await authFetch(`${API_BASE}/api/leads/selling/${selectedSoldEntry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Failed to update source');

      setSelectedSoldEntry({ ...selectedSoldEntry, source: updatedSource });
      setSoldEntries((prev) => prev.map((entry) => (
        entry.id === selectedSoldEntry.id ? { ...entry, source: updatedSource } : entry
      )));
    } catch (err) {
      console.error('Error updating source:', err);
      alert('Could not update source.');
    }
  };

  const groupedShowings = showings.reduce((acc, showing) => {
    const key = `${showing.sellingPropertyId || ''}__${showing.showingDateTime || ''}`;
    if (!acc[key]) {
      acc[key] = {
        ...showing,
        people: []
      };
    }
    acc[key].people.push({
      name: showing.name,
      email: showing.email,
      phone: showing.phone
    });
    return acc;
  }, {});

  const groupedShowingsList = Object.values(groupedShowings).sort(
    (a, b) => new Date(b.showingDateTime) - new Date(a.showingDateTime)
  );

  const filteredShowingsList = groupedShowingsList.filter((showing) => {
    if (!showing.showingDateTime) return true;
    const d = new Date(showing.showingDateTime);
    if (showingsFilter.year && d.getFullYear() !== Number(showingsFilter.year)) return false;
    if (showingsFilter.month && (d.getMonth() + 1) !== Number(showingsFilter.month)) return false;
    return true;
  });

  const showingsYears = [...new Set(
    groupedShowingsList
      .map((s) => s.showingDateTime ? new Date(s.showingDateTime).getFullYear() : null)
      .filter(Boolean)
  )].sort((a, b) => b - a);

  const sortedSellingEntries = [...sellingEntries]
    .map((entry) => ({ ...entry, type: normalizeSellingPropertyType(entry.type) }))
    .filter((entry) => sellingTableFilter === 'all' || entry.type === sellingTableFilter)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const sortedSoldEntries = [...soldEntries].sort(
    (a, b) => new Date(b.soldAt || 0) - new Date(a.soldAt || 0)
  );

  const usedSources = new Set(
    [...sellingEntries, ...soldEntries]
      .map((entry) => entry.source)
      .filter(Boolean)
  );

  const isSourceDeletable = (source) => source && !usedSources.has(source);

  const persistSourceOptions = (options) => {
    setSourceOptions(options);
    try {
      localStorage.setItem('sellingSourceOptions', JSON.stringify(options));
    } catch (err) {
      console.warn('Failed to store source options', err);
    }
  };

  const handleAddSourceOption = (currentSource, setSource) => {
    const value = window.prompt('New source');
    const trimmed = value ? value.trim() : '';
    if (!trimmed) return;
    if (!sourceOptions.includes(trimmed)) {
      persistSourceOptions([...sourceOptions, trimmed]);
    }
    setSource(trimmed);
  };

  const handleDeleteSourceOption = (currentSource, setSource) => {
    if (!isSourceDeletable(currentSource)) return;
    const updated = sourceOptions.filter((option) => option !== currentSource);
    persistSourceOptions(updated);
    setSource(updated[0] || '');
  };

  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

  const formatEuro = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return `${value} €`;
    return `${Math.round(num).toLocaleString('sl-SI')} €`;
  };

  const parseNumberValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const computeCommissionValue = ({ value, type, base }) => {
    const parsedValue = parseNumberValue(value);
    if (parsedValue === null) return null;
    if (type === '%') {
      const parsedBase = parseNumberValue(base);
      if (parsedBase === null) return null;
      return (parsedValue / 100) * parsedBase;
    }
    return parsedValue;
  };

  const computePercentValue = ({ value, base }) => {
    const parsedValue = parseNumberValue(value);
    const parsedBase = parseNumberValue(base);
    if (parsedValue === null || parsedBase === null || parsedBase === 0) return null;
    return (parsedValue / parsedBase) * 100;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return '';
    return `${parsed.toFixed(2)} %`;
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '';
    const diffMs = new Date(end) - new Date(start);
    if (Number.isNaN(diffMs) || diffMs < 0) return '';
    const totalHours = Math.floor(diffMs / 3600000);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    if (days === 0) return `${hours} ur`;
    if (hours === 0) return `${days} dni`;
    return `${days} dni ${hours} ur`;
  };

  return (
    <div style={{ maxWidth: '1200px' }}>
      <style>{`
        @media (max-width: 768px) {
          .responsive-table {
            display: block;
            width: 100%;
          }
          .responsive-table thead {
            display: none;
          }
          .responsive-table tbody,
          .responsive-table tr,
          .responsive-table td {
            display: block;
            width: 100%;
          }
          .responsive-table tr {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            margin-bottom: 12px;
            padding: 8px;
            background: #fff;
          }
          .responsive-table td {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            padding: 6px 8px;
            font-size: 13px;
            border: none !important;
          }
          .responsive-table td::before {
            content: attr(data-label);
            font-weight: 600;
            color: #64748b;
            flex: 0 0 45%;
          }
        }
      `}</style>
      <h1 style={{ color: '#1e293b', marginBottom: '10px' }}>Dashboard</h1>
      <p style={{ color: '#64748b', marginBottom: '24px' }}>Overview of lead generation activity.</p>

      {isUserB && <GoalsCard isMobile={isMobile} />}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#1e293b', fontSize: '18px' }}>
          Lead Generation - Interesting Properties
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Created by</label>
            <select
              value={leadStatsFilters.creator}
              onChange={(e) => setLeadStatsFilters({ ...leadStatsFilters, creator: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            >
              <option value="">All users</option>
              {leadStatsOptions.creators.map((creator) => (
                <option key={creator} value={creator}>{creator}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Month</label>
            <select
              value={leadStatsFilters.month}
              onChange={(e) => setLeadStatsFilters({ ...leadStatsFilters, month: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            >
              <option value="">All months</option>
              {leadStatsOptions.months.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Property type</label>
            <select
              value={leadStatsFilters.type}
              onChange={(e) => setLeadStatsFilters({ ...leadStatsFilters, type: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
            >
              <option value="">All types</option>
              {leadStatsOptions.types.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px'
        }}>
          <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Total entered</div>
            <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{leadStatsLoading ? '…' : leadStats.total}</div>
          </div>
          <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Letters sent (pošta poslana)</div>
            <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{leadStatsLoading ? '…' : leadStats.lettersSent}</div>
          </div>
          <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Responded (so se odzvali)</div>
            <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{leadStatsLoading ? '…' : leadStats.responded}</div>
          </div>
          <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>In selling (imam v prodaji)</div>
            <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{leadStatsLoading ? '…' : leadStats.inSelling}</div>
          </div>
        </div>
        {leadStatsError && (
          <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '13px' }}>{leadStatsError}</div>
        )}
      </div>

      {isUserB && (
        <div style={{
          marginTop: '30px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', gap: '0', marginBottom: '16px' }}>
            {[
              { key: 'trenutna_prodaja', label: 'Trenutna prodaja' },
              { key: 'pridobivanje', label: 'Pridobivanje v prodajo' },
              { key: 'prodano', label: 'Prodano' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setSellingStatsTab(tab.key);
                  setSellingStatsFilter({ source: '', year: '', month: '' });
                }}
                style={{
                  padding: '10px 18px',
                  backgroundColor: sellingStatsTab === tab.key ? '#2563eb' : '#f1f5f9',
                  color: sellingStatsTab === tab.key ? 'white' : '#475569',
                  border: '1px solid ' + (sellingStatsTab === tab.key ? '#2563eb' : '#e2e8f0'),
                  borderRadius: tab.key === 'trenutna_prodaja' ? '8px 0 0 8px' : tab.key === 'prodano' ? '0 8px 8px 0' : '0',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Vir (source)</label>
              <select
                value={sellingStatsFilter.source}
                onChange={(e) => setSellingStatsFilter({ ...sellingStatsFilter, source: e.target.value })}
                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '140px' }}
              >
                <option value="">Vsi viri</option>
                {(sellingStats.sources || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            {(sellingStatsTab === 'pridobivanje' || sellingStatsTab === 'prodano') && (
              <>
                <div>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Leto</label>
                  <select
                    value={sellingStatsFilter.year}
                    onChange={(e) => setSellingStatsFilter({ ...sellingStatsFilter, year: e.target.value })}
                    style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '100px' }}
                  >
                    <option value="">Vsa leta</option>
                    {(sellingStats.years || []).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Mesec</label>
                  <select
                    value={sellingStatsFilter.month}
                    onChange={(e) => setSellingStatsFilter({ ...sellingStatsFilter, month: e.target.value })}
                    style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '100px' }}
                  >
                    <option value="">Vsi meseci</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <option key={m} value={m}>{['Januar','Februar','Marec','April','Maj','Junij','Julij','Avgust','September','Oktober','November','December'][m-1]}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '0'
          }}>
            <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Število nepremičnin</div>
              <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{sellingStatsLoading ? '…' : sellingStats.count}</div>
            </div>
            <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Vsota cen</div>
              <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{sellingStatsLoading ? '…' : `${sellingStats.sumPrice.toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}</div>
            </div>
            <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Vsota bruto provizij</div>
              <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{sellingStatsLoading ? '…' : `${sellingStats.sumGross.toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}</div>
            </div>
            <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Vsota neto provizij</div>
              <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{sellingStatsLoading ? '…' : `${sellingStats.sumNet.toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}</div>
            </div>
            <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Povprečna bruto provizija</div>
              <div style={{ fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>{sellingStatsLoading ? '…' : `${sellingStats.avgGross.toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`}</div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{sellingStatsLoading ? '' : `${sellingStats.avgGrossPercent.toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`}</div>
            </div>
          </div>
        </div>
      )}

      {isUserB && (
        <div style={{
          marginTop: '30px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>Properties in Selling</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Type</label>
                <select
                  value={sellingTableFilter}
                  onChange={(event) => setSellingTableFilter(event.target.value)}
                  style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '150px' }}
                >
                  <option value="all">All types</option>
                  {SELLING_PROPERTY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => {
                  setShowShowingsModal(true);
                  fetchShowingsList();
                }}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#f1f5f9',
                  color: '#1e293b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Ogledi
              </button>
              <button
                onClick={() => setShowSoldModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#f1f5f9',
                  color: '#1e293b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Sold
              </button>
              <button
                onClick={openNewSellingModal}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Add entry
              </button>
            </div>
          </div>
          {sellingError && (
            <div style={{ marginBottom: '10px', color: '#dc2626', fontSize: '13px' }}>{sellingError}</div>
          )}
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: isMobile ? '60vh' : '70vh' }}>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sellingLoading ? (
                  <div style={{ padding: '15px', color: '#94a3b8' }}>Loading...</div>
                ) : sortedSellingEntries.length === 0 ? (
                  <div style={{ padding: '15px', color: '#94a3b8' }}>No properties in selling yet.</div>
                ) : (
                  sortedSellingEntries.map((entry) => (
                    <div
                      key={entry.id}
                      onClick={() => openSellingModal(entry)}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{entry.katastrskaObcina}</div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>{entry.idParceleStavbe}</div>
                      </div>
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#1e293b' }}>
                        <div>Tip nepremičnine: {entry.propertyType || '—'}</div>
                        <div style={{ whiteSpace: 'pre-line' }}>
                          Owners: {(entry.ownersText || entry.ownerNames || '')
                            .split(/[,;\n]+/)
                            .map((name) => name.trim())
                            .filter(Boolean)
                            .join('\n')}
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '13px', color: '#475569' }}>
                        <div>Cena: {formatEuro(entry.price)}</div>
                        <div>Bruto provizija: {formatEuro(entry.grossCommission)}</div>
                        <div>Neto provizija: {formatEuro(entry.netCommission)}</div>
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            openSellingModal(entry, 'showing');
                          }}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#0ea5e9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Ogled
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            openSellingModal(entry, 'contact');
                          }}
                          style={{
                            marginLeft: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Kontakt
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Katastrska občina</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>ID parcele / stavbe</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Tip nepremičnine</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Owners</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Cena (€)</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Bruto provizija (€)</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Neto provizija (€)</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Ogled</th>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Kontakt</th>
                  </tr>
                </thead>
                <tbody>
                  {sellingLoading ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '15px', color: '#94a3b8' }}>Loading...</td>
                    </tr>
                  ) : sortedSellingEntries.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '15px', color: '#94a3b8' }}>No properties in selling yet.</td>
                    </tr>
                  ) : (
                    sortedSellingEntries.map((entry) => (
                      <tr
                        key={entry.id}
                        onClick={() => openSellingModal(entry)}
                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      >
                        <td data-label="Katastrska občina" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.katastrskaObcina}</td>
                        <td data-label="ID parcele / stavbe" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.idParceleStavbe}</td>
                        <td data-label="Tip nepremičnine" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.propertyType || '—'}</td>
                        <td data-label="Owners" style={{ padding: '10px', fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-line' }}>
                          {(entry.ownersText || entry.ownerNames || '')
                            .split(/[,;\n]+/)
                            .map((name) => name.trim())
                            .filter(Boolean)
                            .join('\n')}
                        </td>
                        <td data-label="Cena" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.price)}</td>
                        <td data-label="Bruto provizija" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.grossCommission)}</td>
                        <td data-label="Neto provizija" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.netCommission)}</td>
                        <td data-label="Ogled" style={{ padding: '10px' }}>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              openSellingModal(entry, 'showing');
                            }}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            Ogled
                          </button>
                        </td>
                        <td data-label="Kontakt" style={{ padding: '10px' }}>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              openSellingModal(entry, 'contact');
                            }}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            Kontakt
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showSellingModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e293b' }}>{isNewSellingEntry ? 'Add selling entry' : 'Edit selling entry'}</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Podpis posr. pogodbe</label>
                <input
                  type="datetime-local"
                  value={sellingForm.createdAt ? new Date(new Date(sellingForm.createdAt).getTime() - new Date(sellingForm.createdAt).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setSellingForm({ ...sellingForm, createdAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Katastrska občina *</label>
                <input
                  value={sellingForm.katastrskaObcina}
                  onChange={(e) => setSellingForm({ ...sellingForm, katastrskaObcina: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>ID parcele / stavbe *</label>
                <input
                  value={sellingForm.idParceleStavbe}
                  onChange={(e) => setSellingForm({ ...sellingForm, idParceleStavbe: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Tip *</label>
                <select
                  value={sellingForm.type}
                  onChange={(e) => setSellingForm({ ...sellingForm, type: normalizeSellingPropertyType(e.target.value) })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                >
                  <option value="">Select type</option>
                  {SELLING_PROPERTY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="checkbox"
                    checked={sellingForm.advertisingEnabled !== false}
                    onChange={(e) => setSellingForm({ ...sellingForm, advertisingEnabled: e.target.checked })}
                  />
                  <span>Oglašujemo</span>
                </label>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Tip nepremičnine</label>
                <select
                  value={sellingForm.propertyType || ''}
                  onChange={(e) => setSellingForm({ ...sellingForm, propertyType: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                >
                  <option value="">Select property type</option>
                  {SELLING_PROPERTY_REAL_ESTATE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Površina (m²)</label>
                <input
                  type="number"
                  value={sellingForm.surfaceArea || ''}
                  onChange={(e) => setSellingForm({ ...sellingForm, surfaceArea: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              {sellingForm.advertisingEnabled ? (
                <div>
                  <label style={{ fontSize: '12px', color: '#64748b' }}>Link do oglasa</label>
                  <input
                    type="url"
                    value={sellingForm.adLink || ''}
                    onChange={(e) => setSellingForm({ ...sellingForm, adLink: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    placeholder="https://example.com"
                  />
                </div>
              ) : null}
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Cena (€)</label>
                <input
                  value={sellingForm.price}
                  onChange={(e) => setSellingForm({ ...sellingForm, price: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Bruto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={sellingForm.grossCommissionType === '%' ? sellingForm.grossCommissionInput : sellingForm.grossCommission}
                    onChange={(e) => setSellingForm(
                      sellingForm.grossCommissionType === '%'
                        ? { ...sellingForm, grossCommissionInput: e.target.value }
                        : { ...sellingForm, grossCommission: e.target.value }
                    )}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <select
                    value={sellingForm.grossCommissionType}
                    onChange={(e) => setSellingForm({ ...sellingForm, grossCommissionType: e.target.value })}
                    style={{ width: '70px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  {sellingForm.grossCommissionType === '%'
                    ? `${formatEuro(computeCommissionValue({
                        value: sellingForm.grossCommissionInput,
                        type: '%',
                        base: sellingForm.price
                      }))}`
                    : formatPercent(computePercentValue({
                        value: sellingForm.grossCommission,
                        base: sellingForm.price
                      }))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Neto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={sellingForm.netCommissionType === '%' ? sellingForm.netCommissionInput : sellingForm.netCommission}
                    onChange={(e) => setSellingForm(
                      sellingForm.netCommissionType === '%'
                        ? { ...sellingForm, netCommissionInput: e.target.value }
                        : { ...sellingForm, netCommission: e.target.value }
                    )}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <select
                    value={sellingForm.netCommissionType}
                    onChange={(e) => setSellingForm({ ...sellingForm, netCommissionType: e.target.value })}
                    style={{ width: '70px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  {sellingForm.netCommissionType === '%'
                    ? `${formatEuro(computeCommissionValue({
                        value: sellingForm.netCommissionInput,
                        type: '%',
                        base: computeCommissionValue({
                          value: sellingForm.grossCommissionType === '%' ? sellingForm.grossCommissionInput : sellingForm.grossCommission,
                          type: sellingForm.grossCommissionType,
                          base: sellingForm.price
                        })
                      }))}`
                    : formatPercent(computePercentValue({
                        value: sellingForm.netCommission,
                        base: computeCommissionValue({
                          value: sellingForm.grossCommissionType === '%' ? sellingForm.grossCommissionInput : sellingForm.grossCommission,
                          type: sellingForm.grossCommissionType,
                          base: sellingForm.price
                        })
                      }))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Vir (source)</label>
                <select
                  value={sellingForm.source}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '__add__') {
                      handleAddSourceOption(sellingForm.source, (next) => setSellingForm({ ...sellingForm, source: next }));
                      return;
                    }
                    if (value === '__delete__') {
                      handleDeleteSourceOption(sellingForm.source, (next) => setSellingForm({ ...sellingForm, source: next }));
                      return;
                    }
                    setSellingForm({ ...sellingForm, source: value });
                  }}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                >
                  {sourceOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                  <option value="__add__">Add new source…</option>
                  {isSourceDeletable(sellingForm.source) && (
                    <option value="__delete__">Delete selected source</option>
                  )}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Owners</label>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(sellingForm.owners || []).map((owner, ownerIndex) => (
                    <div key={`selling-owner-${ownerIndex}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        value={owner.name}
                        onChange={(e) => {
                          const nextOwners = [...(sellingForm.owners || [])];
                          nextOwners[ownerIndex] = { ...nextOwners[ownerIndex], name: e.target.value };
                          setSellingForm({
                            ...sellingForm,
                            owners: nextOwners,
                            ownersText: ownerDraftsToText(nextOwners)
                          });
                        }}
                        style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                        placeholder={`Owner ${ownerIndex + 1}`}
                      />
                      {(sellingForm.owners || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const nextOwners = (sellingForm.owners || []).filter((_, index) => index !== ownerIndex);
                            setSellingForm({
                              ...sellingForm,
                              owners: nextOwners.length > 0 ? nextOwners : [createOwnerDraft()],
                              ownersText: ownerDraftsToText(nextOwners)
                            });
                          }}
                          style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setSellingForm({
                    ...sellingForm,
                    owners: [...(sellingForm.owners || []), createOwnerDraft()]
                  })}
                  style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                >
                  Add another owner
                </button>
              </div>
              {sellingForm.leadId && (
                <a
                  href={`/real-estate/lead-gen?leadId=${sellingForm.leadId}`}
                  style={{ color: '#2563eb', fontSize: '13px', textDecoration: 'none' }}
                >
                  Open lead details in Lead Gen
                </a>
              )}
              {!isNewSellingEntry && (
                <div>
                  <label style={{ fontSize: '12px', color: '#64748b' }}>Ogledi</label>
                  {showingsError && (
                    <div style={{ color: '#dc2626', fontSize: '12px' }}>{showingsError}</div>
                  )}
                  {showingsLoading ? (
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>Loading...</div>
                  ) : groupedShowingsList.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>No ogledi yet.</div>
                  ) : (
                    <ul style={{ margin: '6px 0 0', paddingLeft: '16px', fontSize: '12px', color: '#1e293b' }}>
                      {groupedShowingsList.map((showing) => (
                        <li key={showing.id}>
                          {new Date(showing.showingDateTime).toLocaleString()} — {(showing.people || [])
                            .map((person) => person.name)
                            .filter(Boolean)
                            .join(', ')}
                          {showing.people?.some((person) => person.phone) ? `, ${showing.people
                            .map((person) => person.phone)
                            .filter(Boolean)
                            .join(', ')}` : ''}
                          {showing.people?.some((person) => person.email) ? `, ${showing.people
                            .map((person) => person.email)
                            .filter(Boolean)
                            .join(', ')}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={handleSaveSellingEntry}
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '10px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Save
              </button>
              {!isNewSellingEntry && (
                <button
                  onClick={() => {
                    setMarkSoldForm({
                      price: sellingForm.price ?? '',
                      grossCommission: sellingForm.grossCommission ?? '',
                      netCommission: sellingForm.netCommission ?? '',
                      grossCommissionType: sellingForm.grossCommissionType || '€',
                      netCommissionType: sellingForm.netCommissionType || '€',
                      grossCommissionInput: sellingForm.grossCommissionInput ?? '',
                      netCommissionInput: sellingForm.netCommissionInput ?? '',
                      buyersText: sellingForm.buyersText ?? ''
                    });
                    setShowMarkSoldModal(true);
                  }}
                  style={{
                    flex: 1,
                    minWidth: '140px',
                    padding: '10px',
                    backgroundColor: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Mark as sold
                </button>
              )}
              {!isNewSellingEntry && (
                <button
                  onClick={handleDeleteSellingEntry}
                  style={{
                    flex: 1,
                    minWidth: '140px',
                    padding: '10px',
                    backgroundColor: '#fee2e2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => setShowSellingModal(false)}
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '10px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSoldModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '900px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Sold properties</h3>
              <button
                onClick={() => setShowSoldModal(false)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            {soldError && (
              <div style={{ marginBottom: '10px', color: '#dc2626', fontSize: '13px' }}>{soldError}</div>
            )}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: isMobile ? '60vh' : '70vh' }}>
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {soldLoading ? (
                    <div style={{ padding: '15px', color: '#94a3b8' }}>Loading...</div>
                  ) : sortedSoldEntries.length === 0 ? (
                    <div style={{ padding: '15px', color: '#94a3b8' }}>No sold properties yet.</div>
                  ) : (
                    sortedSoldEntries.map((entry) => (
                      <div
                        key={entry.id}
                        onClick={() => openSoldDetailsModal(entry)}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '14px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{entry.katastrskaObcina}</div>
                          <div style={{ color: '#64748b', fontSize: '12px' }}>{entry.idParceleStavbe}</div>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#1e293b' }}>
                          <div>Tip: {entry.type}</div>
                          <div style={{ whiteSpace: 'pre-line' }}>
                            Owners: {(entry.ownersText || entry.ownerNames || '')
                              .split(/[,;\n]+/)
                              .map((name) => name.trim())
                              .filter(Boolean)
                              .join('\n')}
                          </div>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#475569' }}>
                          <div>Cena: {formatEuro(entry.price)}</div>
                          <div>Bruto provizija: {formatEuro(entry.grossCommission)}</div>
                          <div>Neto provizija: {formatEuro(entry.netCommission)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Katastrska občina</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>ID parcele / stavbe</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Tip</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Owners</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Cena (€)</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Bruto provizija (€)</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Neto provizija (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soldLoading ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '15px', color: '#94a3b8' }}>Loading...</td>
                      </tr>
                    ) : sortedSoldEntries.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '15px', color: '#94a3b8' }}>No sold properties yet.</td>
                      </tr>
                    ) : (
                      sortedSoldEntries.map((entry) => (
                        <tr
                          key={entry.id}
                          style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                          onClick={() => openSoldDetailsModal(entry)}
                        >
                          <td data-label="Katastrska občina" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.katastrskaObcina}</td>
                          <td data-label="ID parcele / stavbe" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.idParceleStavbe}</td>
                          <td data-label="Tip" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{entry.type}</td>
                          <td data-label="Owners" style={{ padding: '10px', fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-line' }}>
                            {(entry.ownersText || entry.ownerNames || '')
                              .split(/[,;\n]+/)
                              .map((name) => name.trim())
                              .filter(Boolean)
                              .join('\n')}
                          </td>
                          <td data-label="Cena" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.price)}</td>
                          <td data-label="Bruto provizija" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.grossCommission)}</td>
                          <td data-label="Neto provizija" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{formatEuro(entry.netCommission)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showShowingsModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '900px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Ogledi</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setShowingsEditMode(!showingsEditMode);
                    setEditingShowings({});
                  }}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: showingsEditMode ? '#2563eb' : '#f1f5f9',
                    color: showingsEditMode ? 'white' : '#1e293b',
                    border: '1px solid ' + (showingsEditMode ? '#2563eb' : '#e2e8f0'),
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {showingsEditMode ? 'Done' : 'Edit'}
                </button>
                <button
                  onClick={() => setShowShowingsModal(false)}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Leto</label>
                <select
                  value={showingsFilter.year}
                  onChange={(e) => setShowingsFilter({ ...showingsFilter, year: e.target.value })}
                  style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '100px' }}
                >
                  <option value="">Vsa leta</option>
                  {showingsYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Mesec</label>
                <select
                  value={showingsFilter.month}
                  onChange={(e) => setShowingsFilter({ ...showingsFilter, month: e.target.value })}
                  style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', minWidth: '100px' }}
                >
                  <option value="">Vsi meseci</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <option key={m} value={m}>{['Januar','Februar','Marec','April','Maj','Junij','Julij','Avgust','September','Oktober','November','December'][m-1]}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', paddingBottom: '7px' }}>
                Skupaj: {filteredShowingsList.length}
              </div>
            </div>
            {showingsError && (
              <div style={{ marginBottom: '10px', color: '#dc2626', fontSize: '13px' }}>{showingsError}</div>
            )}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: isMobile ? '60vh' : '70vh' }}>
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {showingsLoading ? (
                    <div style={{ padding: '15px', color: '#94a3b8' }}>Loading...</div>
                  ) : filteredShowingsList.length === 0 ? (
                    <div style={{ padding: '15px', color: '#94a3b8' }}>No ogledi yet.</div>
                  ) : (
                    filteredShowingsList.map((showing) => (
                      <div key={showing.id} style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '14px',
                        backgroundColor: 'white'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>
                            {(showing.people || [])
                              .map((person) => person.name)
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                          <div style={{ color: '#64748b', fontSize: '12px' }}>{new Date(showing.showingDateTime).toLocaleString()}</div>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#475569' }}>
                          {showing.people?.some((person) => person.email) ? (
                            <div>
                              Email:{' '}
                              {showing.people
                                .map((person) => person.email)
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          ) : null}
                          {showing.people?.some((person) => person.phone) ? (
                            <div>
                              Phone:{' '}
                              {showing.people
                                .map((person) => person.phone)
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          ) : null}
                          {showing.notes ? <div>Opombe: {showing.notes}</div> : null}
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#1e293b' }}>
                          <div>Tip: {showing.type || ''}</div>
                          <div style={{ whiteSpace: 'pre-line' }}>
                            Owners: {(showing.ownersText || '')
                              .split(/[,;\n]+/)
                              .map((name) => name.trim())
                              .filter(Boolean)
                              .join('\n')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Date & Time</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Phone</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Katastrska občina</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>ID parcele / stavbe</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Tip</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Owners</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Opombe</th>
                      {showingsEditMode && <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {showingsLoading ? (
                      <tr>
                        <td colSpan={showingsEditMode ? 10 : 9} style={{ padding: '15px', color: '#94a3b8' }}>Loading...</td>
                      </tr>
                    ) : filteredShowingsList.length === 0 ? (
                      <tr>
                        <td colSpan={showingsEditMode ? 10 : 9} style={{ padding: '15px', color: '#94a3b8' }}>No ogledi yet.</td>
                      </tr>
                    ) : (
                      filteredShowingsList.map((showing) => {
                        const origIds = showings.filter(
                          (s) => s.sellingPropertyId === showing.sellingPropertyId && s.showingDateTime === showing.showingDateTime
                        );
                        const anyEditing = origIds.some((r) => editingShowings[r.id]);
                        const inputStyle = { width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '13px' };
                        return (
                          <tr key={showing.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                            <td data-label="Date & Time" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>
                              {anyEditing && origIds[0] && editingShowings[origIds[0].id] ? (
                                <input type="datetime-local" value={editingShowings[origIds[0].id].showingDateTime} onChange={(e) => {
                                  const updated = { ...editingShowings };
                                  origIds.forEach((r) => { if (updated[r.id]) updated[r.id] = { ...updated[r.id], showingDateTime: e.target.value }; });
                                  setEditingShowings(updated);
                                }} style={inputStyle} />
                              ) : new Date(showing.showingDateTime).toLocaleString()}
                            </td>
                            <td data-label="Name" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>
                              {anyEditing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {origIds.map((r, i) => editingShowings[r.id] ? (
                                    <input key={r.id} value={editingShowings[r.id].name} onChange={(e) => setEditingShowings({ ...editingShowings, [r.id]: { ...editingShowings[r.id], name: e.target.value } })} style={inputStyle} />
                                  ) : (
                                    <span key={r.id}>{(showing.people || [])[i]?.name || ''}</span>
                                  ))}
                                </div>
                              ) : (showing.people || []).map((p) => p.name).filter(Boolean).join(', ')}
                            </td>
                            <td data-label="Email" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>
                              {anyEditing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {origIds.map((r, i) => editingShowings[r.id] ? (
                                    <input key={r.id} value={editingShowings[r.id].email} onChange={(e) => setEditingShowings({ ...editingShowings, [r.id]: { ...editingShowings[r.id], email: e.target.value } })} style={inputStyle} />
                                  ) : (
                                    <span key={r.id}>{(showing.people || [])[i]?.email || ''}</span>
                                  ))}
                                </div>
                              ) : (showing.people || []).map((p) => p.email).filter(Boolean).join(', ')}
                            </td>
                            <td data-label="Phone" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>
                              {anyEditing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {origIds.map((r, i) => editingShowings[r.id] ? (
                                    <input key={r.id} value={editingShowings[r.id].phone} onChange={(e) => setEditingShowings({ ...editingShowings, [r.id]: { ...editingShowings[r.id], phone: e.target.value } })} style={inputStyle} />
                                  ) : (
                                    <span key={r.id}>{(showing.people || [])[i]?.phone || ''}</span>
                                  ))}
                                </div>
                              ) : (showing.people || []).map((p) => p.phone).filter(Boolean).join(', ')}
                            </td>
                            <td data-label="Katastrska občina" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{showing.katastrskaObcina || ''}</td>
                            <td data-label="ID parcele / stavbe" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{showing.idParceleStavbe || ''}</td>
                            <td data-label="Tip" style={{ padding: '10px', fontSize: '14px', color: '#1e293b' }}>{showing.type || ''}</td>
                            <td data-label="Owners" style={{ padding: '10px', fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-line' }}>
                              {(showing.ownersText || '').split(/[,;\n]+/).map((n) => n.trim()).filter(Boolean).join('\n')}
                            </td>
                            <td data-label="Opombe" style={{ padding: '10px', fontSize: '13px', color: '#1e293b' }}>
                              {anyEditing && origIds[0] && editingShowings[origIds[0].id] ? (
                                <input value={editingShowings[origIds[0].id].notes} onChange={(e) => {
                                  const updated = { ...editingShowings };
                                  origIds.forEach((r) => { if (updated[r.id]) updated[r.id] = { ...updated[r.id], notes: e.target.value }; });
                                  setEditingShowings(updated);
                                }} style={inputStyle} />
                              ) : (showing.notes || '')}
                            </td>
                            {showingsEditMode && (
                              <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                                {anyEditing ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <button onClick={async () => {
                                      for (const r of origIds) {
                                        if (editingShowings[r.id]) await handleSaveEditedShowing(r.id);
                                      }
                                    }} style={{ padding: '4px 8px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Save</button>
                                    <button onClick={() => {
                                      const next = { ...editingShowings };
                                      origIds.forEach((r) => delete next[r.id]);
                                      setEditingShowings(next);
                                    }} style={{ padding: '4px 8px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <button onClick={() => {
                                      const edits = {};
                                      origIds.forEach((r, i) => {
                                        const p = (showing.people || [])[i] || {};
                                        edits[r.id] = {
                                          showingDateTime: r.showingDateTime ? new Date(new Date(r.showingDateTime).getTime() - new Date(r.showingDateTime).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
                                          name: p.name || '', email: p.email || '', phone: p.phone || '', notes: showing.notes || ''
                                        };
                                      });
                                      setEditingShowings((prev) => ({ ...prev, ...edits }));
                                    }} style={{ padding: '4px 8px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                                    <button onClick={async () => {
                                      if (!window.confirm('Delete this ogled (all people)?')) return;
                                      for (const r of origIds) await handleDeleteShowing(r.id, true);
                                      await fetchShowingsList();
                                    }} style={{ padding: '4px 8px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Del</button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddShowingModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e293b' }}>Add ogled</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Date & Time *</label>
                <input
                  type="datetime-local"
                  value={showingForm.showingDateTime}
                  onChange={(e) => setShowingForm({ ...showingForm, showingDateTime: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              {(showingForm.people || []).map((person, index) => (
                <div key={index} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Person {index + 1}</span>
                    {(showingForm.people || []).length > 1 && (
                      <button
                        onClick={() => {
                          const updated = showingForm.people.filter((_, idx) => idx !== index);
                          setShowingForm({ ...showingForm, people: updated });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#64748b' }}>Name *</label>
                      <input
                        value={person.name}
                        onChange={(e) => {
                          const updated = [...showingForm.people];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setShowingForm({ ...showingForm, people: updated });
                        }}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#64748b' }}>Email</label>
                      <input
                        type="email"
                        value={person.email}
                        onChange={(e) => {
                          const updated = [...showingForm.people];
                          updated[index] = { ...updated[index], email: e.target.value };
                          setShowingForm({ ...showingForm, people: updated });
                        }}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#64748b' }}>Phone</label>
                      <input
                        value={person.phone}
                        onChange={(e) => {
                          const updated = [...showingForm.people];
                          updated[index] = { ...updated[index], phone: e.target.value };
                          setShowingForm({ ...showingForm, people: updated });
                        }}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {(showingForm.people || []).length < 4 && (
                <button
                  onClick={() => setShowingForm({
                    ...showingForm,
                    people: [...showingForm.people, { name: '', email: '', phone: '' }]
                  })}
                  style={{
                    padding: '8px 10px',
                    backgroundColor: '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#1e293b'
                  }}
                >
                  Add person
                </button>
              )}
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Opombe</label>
                <textarea
                  value={showingForm.notes}
                  onChange={(e) => setShowingForm({ ...showingForm, notes: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    minHeight: '70px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleSaveShowing}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Save
              </button>
              <button
                onClick={() => setShowAddShowingModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkSoldModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '480px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e293b' }}>Mark as sold</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Cena (€)</label>
                <input
                  value={markSoldForm.price}
                  onChange={(e) => setMarkSoldForm({ ...markSoldForm, price: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Bruto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission}
                    onChange={(e) => setMarkSoldForm(
                      markSoldForm.grossCommissionType === '%'
                        ? { ...markSoldForm, grossCommissionInput: e.target.value }
                        : { ...markSoldForm, grossCommission: e.target.value }
                    )}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <select
                    value={markSoldForm.grossCommissionType}
                    onChange={(e) => setMarkSoldForm({ ...markSoldForm, grossCommissionType: e.target.value })}
                    style={{ width: '70px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  {markSoldForm.grossCommissionType === '%'
                    ? `${formatEuro(computeCommissionValue({
                        value: markSoldForm.grossCommissionInput,
                        type: '%',
                        base: markSoldForm.price
                      }))}`
                    : formatPercent(computePercentValue({
                        value: markSoldForm.grossCommission,
                        base: markSoldForm.price
                      }))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Neto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={markSoldForm.netCommissionType === '%' ? markSoldForm.netCommissionInput : markSoldForm.netCommission}
                    onChange={(e) => setMarkSoldForm(
                      markSoldForm.netCommissionType === '%'
                        ? { ...markSoldForm, netCommissionInput: e.target.value }
                        : { ...markSoldForm, netCommission: e.target.value }
                    )}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <select
                    value={markSoldForm.netCommissionType}
                    onChange={(e) => setMarkSoldForm({ ...markSoldForm, netCommissionType: e.target.value })}
                    style={{ width: '70px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  {markSoldForm.netCommissionType === '%'
                    ? `${formatEuro(computeCommissionValue({
                        value: markSoldForm.netCommissionInput,
                        type: '%',
                        base: computeCommissionValue({
                          value: markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission,
                          type: markSoldForm.grossCommissionType,
                          base: markSoldForm.price
                        })
                      }))}`
                    : formatPercent(computePercentValue({
                        value: markSoldForm.netCommission,
                        base: computeCommissionValue({
                          value: markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission,
                          type: markSoldForm.grossCommissionType,
                          base: markSoldForm.price
                        })
                      }))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b' }}>Kupci</label>
                <textarea
                  value={markSoldForm.buyersText}
                  onChange={(e) => setMarkSoldForm({ ...markSoldForm, buyersText: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    minHeight: '70px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleConfirmMarkSold}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => setShowMarkSoldModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSoldDetailsModal && selectedSoldEntry && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '22px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Sold property details</h3>
              <button
                onClick={() => setShowSoldDetailsModal(false)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            <div style={{ display: 'grid', gap: '10px', fontSize: '13px', color: '#1e293b' }}>
              <div><strong>Katastrska občina:</strong> {selectedSoldEntry.katastrskaObcina}</div>
              <div><strong>ID parcele / stavbe:</strong> {selectedSoldEntry.idParceleStavbe}</div>
              <div><strong>Tip:</strong> {selectedSoldEntry.type}</div>
              <div style={{ whiteSpace: 'pre-line' }}>
                <strong>Owners:</strong>{' '}
                {(selectedSoldEntry.ownersText || selectedSoldEntry.ownerNames || '')
                  .split(/[,;\n]+/)
                  .map((name) => name.trim())
                  .filter(Boolean)
                  .join('\n')}
              </div>
              <div>
                <strong>Vir (source):</strong>
                <div style={{ display: 'grid', gap: '6px', marginTop: '6px' }}>
                  <select
                    value={selectedSoldEntry.source || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '__add__') {
                        handleAddSourceOption(selectedSoldEntry.source || '', (next) => setSelectedSoldEntry({ ...selectedSoldEntry, source: next }));
                        return;
                      }
                      if (value === '__delete__') {
                        handleDeleteSourceOption(selectedSoldEntry.source || '', (next) => setSelectedSoldEntry({ ...selectedSoldEntry, source: next }));
                        return;
                      }
                      setSelectedSoldEntry({ ...selectedSoldEntry, source: value });
                    }}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    {[...new Set([selectedSoldEntry.source, ...sourceOptions].filter(Boolean))].map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                    <option value="__add__">Add new source…</option>
                    {isSourceDeletable(selectedSoldEntry.source) && (
                      <option value="__delete__">Delete selected source</option>
                    )}
                  </select>
                  <button
                    onClick={() => handleUpdateSoldSource(selectedSoldEntry.source || '')}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      width: 'fit-content'
                    }}
                  >
                    Save source
                  </button>
                </div>
              </div>
              <div><strong>Cena:</strong> {formatEuro(selectedSoldEntry.price)}</div>
              <div><strong>Bruto provizija:</strong> {formatEuro(selectedSoldEntry.grossCommission)}</div>
              <div><strong>Neto provizija:</strong> {formatEuro(selectedSoldEntry.netCommission)}</div>
              <div style={{ whiteSpace: 'pre-line' }}>
                <strong>Kupci:</strong>{' '}
                {(selectedSoldEntry.buyersText || '').trim() || ''}
              </div>
              <div><strong>Podpis posr. pogodbe:</strong> {formatDateTime(selectedSoldEntry.createdAt)}</div>
              <div><strong>Sold at:</strong> {formatDateTime(selectedSoldEntry.soldAt)}</div>
              <div><strong>Time on market:</strong> {formatDuration(selectedSoldEntry.createdAt, selectedSoldEntry.soldAt)}</div>
              <div><strong>Ogledi count:</strong> {groupedShowingsList.length}</div>
              {selectedSoldEntry.leadId && (
                <a
                  href={`/real-estate/lead-gen?leadId=${selectedSoldEntry.leadId}`}
                  style={{ color: '#2563eb', fontSize: '13px', textDecoration: 'none' }}
                >
                  Open lead details in Lead Gen
                </a>
              )}
              <div>
                <strong>Ogledi:</strong>
                {showingsLoading ? (
                  <div style={{ color: '#94a3b8' }}>Loading...</div>
                ) : groupedShowingsList.length === 0 ? (
                  <div style={{ color: '#94a3b8' }}>No ogledi yet.</div>
                ) : (
                  <ul style={{ margin: '6px 0 0', paddingLeft: '16px' }}>
                    {groupedShowingsList.map((showing) => (
                      <li key={showing.id}>
                        {new Date(showing.showingDateTime).toLocaleString()} — {(showing.people || [])
                          .map((person) => person.name)
                          .filter(Boolean)
                          .join(', ')}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
